import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { unstable_batchedUpdates } from "react-dom";
import { useSelector } from "react-redux";
import {
  Button,
  Drawer,
  Dropdown,
  Empty,
  Input,
  Modal,
  Spin,
  Tooltip,
  message as antdMessage,
} from "antd";
import {
  AimOutlined,
  DashboardOutlined,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  FileOutlined,
  LineChartOutlined,
  MoreOutlined,
  PlusOutlined,
  PushpinOutlined,
  RiseOutlined,
  RobotOutlined,
  SearchOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import Select from "@/components/ResponsiveSelect";
import { buildFacilityPath } from "@/utils/route-context";

import {
  deleteFacilityAiConversation,
  getFacilityAiConversation,
  getFacilityAiConversationMessages,
  listFacilityAiConversations,
  streamFacilityAiChat,
  updateFacilityAiConversation,
  uploadFacilityAiAttachment,
} from "./api";
import ComposerPanel, {
  ATTACHMENT_EXTENSIONS,
  ATTACHMENT_MAX_BYTES,
  AttachmentChip,
  AttachmentList,
  clearFacilityDrafts,
  readStoredDraft,
} from "./ComposerPanel";
import EvidenceDataUsed from "./EvidenceDataUsed";
import "./styles.css";
import {
  citationLeaf,
  normalizeResponseManifest,
} from "./responseManifest";
import {
  formatCheckStatus,
  formatCoverageStatus,
  formatSourceRecordSummary,
  humanizeEvidenceLabel,
} from "./responseData.mjs";
import { useCurrentAiPageContext } from "./pageContext";
import MessageItem, { formatMessageTime } from "./MessageItem";
import { copyText } from "./markdownContext";
import { settleActivity } from "./activity.mjs";

/* React 16 auto-batches only inside React event handlers; SSE callbacks
   arrive from the fetch stream, so each event would otherwise render once
   per setState. Batching (3.2) collapses every SSE event — and the submit
   finally block — into a single render. Logic is unchanged; this only
   groups the updates. */
const withBatch = (fn) => unstable_batchedUpdates(fn);

const sortConversations = (conversations) =>
  [...conversations].sort((left, right) => {
    if (Boolean(left.is_pinned) !== Boolean(right.is_pinned)) {
      return left.is_pinned ? -1 : 1;
    }

    const leftTime = new Date(
      left.last_message_at || left.updated_at || left.created_at || 0
    ).getTime();
    const rightTime = new Date(
      right.last_message_at || right.updated_at || right.created_at || 0
    ).getTime();
    return rightTime - leftTime;
  });

const TRACE_STEP_CODES = new Set(["scope", "intent", "plan", "evidence", "answer"]);
const TRACE_CHILD_CODES = new Set([
  "knowledge_base",
  "facility_database",
  "savings_calculation",
  "business_time",
  "web_search",
  "product_skill",
  "facility_data_analysis",
  "conversation_attachments",
  "facility_file_library",
  "module_electricity",
  "module_gas",
  "module_water",
  "module_workorders",
  "module_equipment",
  "module_facility_users",
  "module_performance_metrics",
  "module_facility",
  "evidence_gap",
  "external_data",
  "external_research",
  "facility_document_analysis",
]);
const TRACE_STATUS = new Set(["running", "completed", "failed", "stopped"]);
const TRACE_TITLES = {
  scope: "Confirm facility scope",
  intent: "Understand request",
  plan: "Plan evidence sources",
  evidence: "Gather and verify evidence",
  answer: "Prepare answer",
};
const TRACE_CHILD_TITLES = {
  knowledge_base: "Product documentation",
  facility_database: "Facility data",
  savings_calculation: "Savings calculation",
  business_time: "Current business time",
  web_search: "Web search",
  product_skill: "Product skill",
  facility_data_analysis: "Facility data analysis",
  conversation_attachments: "Uploaded files",
  facility_file_library: "Facility files",
  module_electricity: "Electricity",
  module_gas: "Gas",
  module_water: "Water",
  module_workorders: "Work orders",
  module_equipment: "Equipment",
  module_facility_users: "Facility users",
  module_performance_metrics: "Performance metrics",
  module_facility: "Facility profile",
  evidence_gap: "Evidence gap check",
  external_data: "External data",
  external_research: "Public web research",
  facility_document_analysis: "Facility documents",
};

const SUGGESTIONS = [
  {
    key: "utility-snapshot",
    icon: DashboardOutlined,
    title: "Utility snapshot",
    description: "Summarize this facility's recent utility usage and costs",
    prompt: "Summarize this facility's recent utility usage and costs.",
  },
  {
    key: "work-order-help",
    icon: ToolOutlined,
    title: "Work order help",
    description: "Show open work orders and anything overdue",
    prompt: "Show open work orders and anything overdue.",
  },
  {
    key: "kpi-explainers",
    icon: LineChartOutlined,
    title: "KPI explainers",
    description: "Explain a facility KPI and how it's calculated",
    prompt: "Explain a facility KPI and how it's calculated.",
  },
  {
    key: "budget-forecast",
    icon: RiseOutlined,
    title: "Budget planning",
    description: "Review cost trends and next-year budget inputs",
    prompt: "Review all available monthly utility cost trends and explain what they support for next year's budget planning.",
  },
];

const emptyProgressTrace = () => ({ version: 1, steps: [], activeStepCode: null, highlights: [] });

const sanitizeProgressText = (value, fallback = "") => {
  const text = String(value || "").replace(/\s+/g, " ").trim().slice(0, 180);
  return text || fallback;
};

const normalizeProgressTrace = (payload) => {
  const rawSteps = Array.isArray(payload?.steps) ? payload.steps : [];
  const seen = new Set();
  const steps = rawSteps.reduce((result, rawStep) => {
    const code = typeof rawStep?.code === "string" ? rawStep.code : "";
    if (!TRACE_STEP_CODES.has(code) || seen.has(code)) return result;
    seen.add(code);
    const status = TRACE_STATUS.has(rawStep.status) ? rawStep.status : "running";
    const children = Array.isArray(rawStep.children)
      ? rawStep.children.reduce((childResult, rawChild) => {
          const childCode = typeof rawChild?.code === "string" ? rawChild.code : "";
          if (!TRACE_CHILD_CODES.has(childCode) || childResult.some((item) => item.code === childCode)) {
            return childResult;
          }
          const childStatus = TRACE_STATUS.has(rawChild.status) ? rawChild.status : "running";
          const title = TRACE_CHILD_TITLES[childCode];
          const fallbackSummary =
            childStatus === "failed"
              ? `${title} failed.`
              : childStatus === "running"
                ? `${title} is running.`
                : `${title} checked.`;
          return [
            ...childResult,
            {
              code: childCode,
              title,
              status: childStatus,
              count: Math.max(0, Math.min(Number(rawChild.count) || 0, 99)),
              summary: sanitizeProgressText(rawChild.summary, fallbackSummary),
            },
          ];
        }, [])
      : [];
    return [
      ...result,
      {
        code,
        title: TRACE_TITLES[code],
        status,
        summary: sanitizeProgressText(rawStep.summary),
        children,
      },
    ];
  }, []);
  const activeStepCode = TRACE_STEP_CODES.has(payload?.active_step_code)
    ? payload.active_step_code
    : steps[steps.length - 1]?.code || null;
  const highlights = Array.isArray(payload?.highlights)
    ? payload.highlights
        .map((item) => sanitizeProgressText(item))
        .filter((item, index, list) => item && list[index - 1] !== item)
        .slice(-8)
    : [];
  return { version: 1, steps, activeStepCode, highlights };
};

const createInitialActivity = () => ({ trace: emptyProgressTrace() });

const getCollectionItems = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data?.results)) return payload.data.results;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const normalizeMessage = (item) => {
  const progressTrace = normalizeProgressTrace(item?.progress_trace);
  const responseManifest = normalizeResponseManifest(
    item?.response_manifest || item?.progress_trace?.response_manifest
  );
  return {
    ...item,
    content_markdown: item?.content_markdown || "",
    activity: progressTrace.steps.length ? { trace: progressTrace } : item?.activity,
    response_manifest: responseManifest,
  };
};

const mergeAnswerContent = (streamedContent, finalContent) => {
  const streamed = typeof streamedContent === "string" ? streamedContent : "";
  const final = typeof finalContent === "string" ? finalContent : "";
  if (!streamed) return final;
  if (!final || final === streamed) return streamed;
  if (final.startsWith(streamed)) return final;
  if (streamed.startsWith(final)) return streamed;
  // A completed frame can be stale or normalized differently by the proxy.
  // Keep the already-rendered stream rather than replacing it with less data.
  return streamed;
};

/* Hoisted (3.10): Intl.DateTimeFormat construction is expensive enough to
   matter when a long sidebar re-renders; one module-level instance is now
   reused for every conversation row (invalid dates still fall back to ""). */
const CONVERSATION_DATE_FORMATTER = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
});

const formatConversationDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return CONVERSATION_DATE_FORMATTER.format(date);
};

const startOfLocalDay = (date) => {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

/* Shared day math for the sidebar date groups (2.2) and the transcript day
   separators (2.3). Bucket keys: "today" | "yesterday" | "prev7" | "prev30"
   | `${year}-${month}` for anything older; null when the value is unusable. */
const dayBucketFor = (value, now = new Date()) => {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) return null;

  const dayDiff = Math.round(
    (startOfLocalDay(now).getTime() - startOfLocalDay(date).getTime()) / 86400000
  );
  if (dayDiff <= 0) return "today";
  if (dayDiff === 1) return "yesterday";
  if (dayDiff <= 7) return "prev7";
  if (dayDiff <= 30) return "prev30";
  return `${date.getFullYear()}-${date.getMonth()}`;
};

/* Exact calendar-day identity (unlike dayBucketFor, "prev7" spans several
   days) — used to decide when a transcript day separator is needed. */
const calendarDayKey = (value) => {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) return null;
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
};

const DAY_SEPARATOR_FORMATTER = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
});

const MONTH_YEAR_FORMATTER = new Intl.DateTimeFormat(undefined, {
  month: "long",
  year: "numeric",
});

const formatDaySeparator = (value, now = new Date()) => {
  const bucket = dayBucketFor(value, now);
  if (!bucket) return "";
  if (bucket === "today") return "Today";
  if (bucket === "yesterday") return "Yesterday";
  return DAY_SEPARATOR_FORMATTER.format(new Date(value));
};

const CONVERSATION_BUCKET_LABELS = {
  today: "Today",
  yesterday: "Yesterday",
  prev7: "Previous 7 days",
  prev30: "Previous 30 days",
};

/* Buckers conversations (already sorted desc by activity, pinned first) into
   ordered render groups: Pinned, Today, Yesterday, Previous 7 days, Previous
   30 days, then month-year labels. Empty buckets are simply never created. */
const groupConversationsByDay = (conversations, { flat = false } = {}) => {
  if (flat) {
    return [{ key: "all", label: "", items: conversations }];
  }

  const now = new Date();
  const groups = [];
  const groupByKey = new Map();
  const groupFor = (key, label) => {
    let group = groupByKey.get(key);
    if (!group) {
      group = { key, label, items: [] };
      groupByKey.set(key, group);
      groups.push(group);
    }
    return group;
  };

  conversations.forEach((conversation) => {
    if (conversation.is_pinned) {
      groupFor("pinned", "Pinned").items.push(conversation);
      return;
    }

    const stamp = conversation.last_message_at || conversation.updated_at;
    const bucket = dayBucketFor(stamp, now);
    if (bucket && CONVERSATION_BUCKET_LABELS[bucket]) {
      groupFor(bucket, CONVERSATION_BUCKET_LABELS[bucket]).items.push(conversation);
    } else if (bucket) {
      groupFor(`month-${bucket}`, MONTH_YEAR_FORMATTER.format(new Date(stamp))).items.push(
        conversation
      );
    } else {
      groupFor("undated", "Conversations").items.push(conversation);
    }
  });
  return groups;
};

const toConversationOption = (conversation) => {
  const title = conversation.title || "Untitled conversation";
  const dateText = formatConversationDate(
    conversation.last_message_at || conversation.updated_at
  );
  return {
    value: conversation.id,
    label: title,
    searchText: `${title} ${dateText}`,
  };
};

/* antd 4 grouped options for the mobile conversation Select. */
const buildConversationSelectOptions = (conversations) =>
  groupConversationsByDay(conversations)
    .filter((group) => group.items.length)
    .map((group) => ({
      label: group.label,
      options: group.items.map(toConversationOption),
    }));

/* Sidebar row dropdown items (3.10): shared by ConversationRow below and
   buildConversationMenu (mobile header). */
const conversationMenuItems = (isPinned) => [
  {
    key: "pin",
    icon: <PushpinOutlined />,
    label: isPinned ? "Unpin" : "Pin",
  },
  {
    key: "rename",
    icon: <EditOutlined />,
    label: "Rename",
  },
  {
    key: "delete",
    icon: <DeleteOutlined />,
    danger: true,
    label: "Delete",
  },
];

/* One sidebar conversation row (3.10), memoized so stream frames and
   transcript updates no longer re-render every row (Dropdown + Tooltip
   included). The Dropdown menu is built here from the module-level config
   above; every handler prop is a stable useCallback, so a row only
   re-renders when its conversation object, active state, or disabled flag
   actually changes. */
const ConversationRow = memo(function ConversationRow({
  conversation,
  isActive,
  disabled,
  onSelect,
  onMenuClick,
}) {
  const conversationStamp = conversation.last_message_at || conversation.updated_at;
  const handleMenuClick = ({ key }) => onMenuClick(conversation, key);
  return (
    <div className={`facility-ai-conversation-row${isActive ? " is-active" : ""}`}>
      <button
        type="button"
        className="facility-ai-conversation-select"
        onClick={() => onSelect(conversation.id)}
        disabled={disabled}
      >
        <span className="facility-ai-conversation-title">
          {conversation.is_pinned && (
            <PushpinOutlined
              className="facility-ai-conversation-pin"
              aria-label="Pinned"
            />
          )}
          <span className="facility-ai-conversation-title-text">
            {conversation.title || "Untitled conversation"}
          </span>
        </span>
        <span className="facility-ai-conversation-date">
          {/* Key on the row's own stamp, not the group: pinned-today rows
              live in the "pinned" group but should still show time-of-day. */}
          {dayBucketFor(conversationStamp) === "today"
            ? formatMessageTime(conversationStamp)
            : formatConversationDate(conversationStamp)}
        </span>
      </button>
      <Dropdown
        menu={{ items: conversationMenuItems(conversation.is_pinned), onClick: handleMenuClick }}
        trigger={["click"]}
        placement="bottomRight"
      >
        <span className={`facility-ai-conversation-menu-wrap${disabled ? " is-disabled" : ""}`}>
          <Tooltip title="More actions">
            <button
              type="button"
              className="facility-ai-conversation-menu"
              aria-label={`Actions for ${conversation.title || "conversation"}`}
              disabled={disabled}
            >
              <MoreOutlined />
            </button>
          </Tooltip>
        </span>
      </Dropdown>
    </div>
  );
});

const PUSH_TO_TALK_ARM_DELAY_MS = 140;

const FacilityAiAssistant = ({
  embedded = false,
  companyId: embeddedCompanyId,
  facilityId: embeddedFacilityId,
}) => {
  const routeParams = useParams();
  const companyId = embeddedCompanyId || routeParams.companyId;
  const facilityId = embeddedFacilityId || routeParams.facilityId;
  const navigate = useNavigate();
  const isPC = useSelector((state) => state.App?.isPC ?? true);
  const userId = useSelector((state) => state.Auth?.userId);
  const [conversations, setConversations] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [renameConversation, setRenameConversation] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameSaving, setRenameSaving] = useState(false);
  const [webSearch, setWebSearch] = useState("auto");
  const [reasoningEffort, setReasoningEffort] = useState("medium");
  const [currentPageEnabled, setCurrentPageEnabled] = useState(false);
  const [sourcesOpen, setSourcesOpen] = useState(null);
  const [sidebarSearch, setSidebarSearch] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [conversationAttachments, setConversationAttachments] = useState([]);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  /* Unread indicators (3.9): count of new rows that arrived while the user
     was scrolled away, plus a dot flag for same-length content changes
     (streaming deltas). Ref mirrors let the scroll handler and effects
     read/reset them without stale closures. */
  const [unreadCount, setUnreadCount] = useState(0);
  const [streamedWhileAway, setStreamedWhileAway] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const pageRootRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const streamControllerRef = useRef(null);
  const scopeRef = useRef("");
  /* Composer island (3.3): the page reaches the extracted ComposerPanel
     (draft, textarea, attachments picker, voice recorder) exclusively
     through this ref — focus/select/getDraft/setDraft/clearDraft/appendText
     plus voiceStart/voiceStop/voiceCancel for the global PTT handler. */
  const composerRef = useRef(null);
  const sidebarSearchRef = useRef(null);
  const attachmentSeqRef = useRef(0);
  const followOutputRef = useRef(true);
  const unreadCountRef = useRef(0);
  const streamedWhileAwayRef = useRef(false);
  /* Previous messages snapshot for the unread tracking in the
     messages-change effect (3.9). */
  const prevMessagesRef = useRef(messages);
  const streamContentRef = useRef({ messageId: null, content: "" });
  const streamFrameRef = useRef(null);
  const copiedTimerRef = useRef(null);
  const lastFacilityIdRef = useRef(null);
  const selectedConversationIdRef = useRef(selectedConversationId);
  selectedConversationIdRef.current = selectedConversationId;
  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;
  /* Latest messages for stable event handlers (Wave 3.1): lets
     handleRegenerateMessage keep a stable identity for the memoized
     MessageItem rows while still reading fresh state at click time. */
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const attachmentsUploading = attachments.some((item) => item.status === "uploading");
  const currentPageContext = useCurrentAiPageContext(companyId, facilityId);

  useEffect(() => {
    if (!currentPageContext) setCurrentPageEnabled(false);
  }, [currentPageContext]);

  const cancelStreamFrame = useCallback(() => {
    if (streamFrameRef.current !== null) {
      cancelAnimationFrame(streamFrameRef.current);
      streamFrameRef.current = null;
    }
  }, []);

  const resetUnreadIndicators = useCallback(() => {
    if (unreadCountRef.current !== 0) {
      unreadCountRef.current = 0;
      setUnreadCount(0);
    }
    if (streamedWhileAwayRef.current) {
      streamedWhileAwayRef.current = false;
      setStreamedWhileAway(false);
    }
  }, []);

  /* Frame-stable auto-scroll (3.5): scrollIntoView schedules a competing
     smooth-scroll animation per stream frame, which visibly fights the
     follow updates. scrollTo({ behavior }) shares one primitive across
     paths and now honors the argument: streaming / conversation-open call
     sites pass "auto" (instant, so nothing fights the per-frame follow),
     the jump button and post-answer follow pass "smooth" (a single
     animation). No JS reduced-motion gate — the instant path is motion-
     free, and "smooth" is one short scroll, not a loop. */
  const scrollToLatest = useCallback(
    (behavior = "smooth", force = false) => {
      if (force) {
        followOutputRef.current = true;
        setShowScrollToBottom(false);
        resetUnreadIndicators();
      }
      requestAnimationFrame(() => {
        const node = messagesContainerRef.current;
        if (!node) return;
        node.scrollTo({ top: node.scrollHeight, behavior });
        // content-visibility: auto keeps off-screen rows as ~160px
        // placeholders, so this frame's scrollHeight understates the
        // document until the bottom rows paint at their real heights
        // (300-800px). A nested second rAF re-clamps scrollTop against
        // the post-render scrollHeight. Instant path only — re-assigning
        // scrollTop during a smooth scroll cancels the animation.
        if (behavior === "auto") {
          requestAnimationFrame(() => {
            const latest = messagesContainerRef.current;
            if (latest) latest.scrollTop = latest.scrollHeight;
          });
        }
      });
    },
    [resetUnreadIndicators]
  );

  /* Voice status mirror (3.3): the recorder hook now lives INSIDE
     ComposerPanel — its 5 Hz elapsedMs ticks re-render only that panel.
     This ref-only mirror feeds the global push-to-talk handler below
     without ever re-rendering the page (the callback only writes the ref).
     Status semantics match the old direct-hook mirror: refreshed after
     commit, while the hook's own synchronous internal guards are
     unchanged. */
  const voiceStatusRef = useRef("idle");
  const handleVoiceStatusChange = useCallback((status) => {
    voiceStatusRef.current = status;
  }, []);
  const voiceHotkeyActiveRef = useRef(false);
  /* Mirrored state for the global keydown handler so it reads current values
     without re-subscribing (same pattern as voiceStatusRef above). */
  const renameConversationRef = useRef(renameConversation);
  renameConversationRef.current = renameConversation;
  const sourcesOpenRef = useRef(sourcesOpen);
  sourcesOpenRef.current = sourcesOpen;
  const isStreamingRef = useRef(isStreaming);
  isStreamingRef.current = isStreaming;

  const handleStop = useCallback(() => {
    streamControllerRef.current?.abort();
  }, []);

  // Global push-to-talk: hold Ctrl/Cmd + Shift together to record, release
  // either to stop. A third key while holding cancels the clip so shortcuts
  // such as Cmd+Shift+T never leave a stuck recording. Recording start is
  // deferred by a short arm delay so any modifier chord (e.g. Cmd+Shift+O)
  // cancels cleanly before audio capture begins.
  //
  // Global shortcuts share this handler. Branch order matters:
  //   1. Esc cancels an active (or armed) push-to-talk clip.
  //   2. Esc stops generation (visible tab only; skipped while rename modal
  //      / sources drawer are open so their own Esc handling wins).
  //   3. Shift+Esc focuses the composer and selects its text (visible tab
  //      only; skipped while a modal/drawer is open).
  //   4. Third-key cancels push-to-talk.
  //   5. Shift + Ctrl/Cmd arms deferred push-to-talk.
  //   6. Ctrl/Cmd+K focuses the sidebar search (visible tab only; also
  //      skipped while typing in an input, on mobile, or while a
  //      modal/drawer is open).
  // The push-to-talk branches stay global (pre-existing behavior). This page
  // lives in a keep-alive DraggableTab, so it stays mounted (display:none)
  // while another app tab is active — the other shortcuts must not fire then.
  useEffect(() => {
    const isModified = (event) => event.metaKey || event.ctrlKey;
    const isEditableTarget = (event) => {
      const target = event.target;
      if (!target) return false;
      return (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      );
    };

    let armTimer = null;
    const clearArmTimer = () => {
      if (armTimer !== null) {
        clearTimeout(armTimer);
        armTimer = null;
      }
    };

    /* Tab visibility via the DOM rather than the keep-alive context: the
       tab pane hides this page with display:none, and offsetParent is null
       only while an ancestor (or the node itself) is display:none (the root
       is not position:fixed). This is independent of how the contextual
       route (/facility/:companyId/:facilityId/assistant) is matched. */
    const isTabVisible = () => pageRootRef.current?.offsetParent != null;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        if (voiceStatusRef.current === "recording" || voiceHotkeyActiveRef.current) {
          voiceHotkeyActiveRef.current = false;
          clearArmTimer();
          composerRef.current?.voiceCancel();
          return;
        }
        if (
          !event.shiftKey &&
          isTabVisible() &&
          isStreaming &&
          !renameConversationRef.current &&
          !sourcesOpenRef.current
        ) {
          event.preventDefault();
          handleStop();
          return;
        }
        if (
          event.shiftKey &&
          isTabVisible() &&
          !renameConversationRef.current &&
          !sourcesOpenRef.current
        ) {
          event.preventDefault();
          // Composer textarea lives in ComposerPanel now — focus + select
          // through the imperative handle (same node, same behavior).
          composerRef.current?.focus();
          composerRef.current?.select();
          return;
        }
        return;
      }

      if (event.repeat) return;

      if (
        voiceHotkeyActiveRef.current &&
        isModified(event) &&
        event.key !== "Meta" &&
        event.key !== "Control" &&
        event.key !== "Shift"
      ) {
        voiceHotkeyActiveRef.current = false;
        clearArmTimer();
        composerRef.current?.voiceCancel();
        return;
      }

      if (
        event.key === "Shift" &&
        isModified(event) &&
        !voiceHotkeyActiveRef.current &&
        voiceStatusRef.current === "idle" &&
        !isStreaming
      ) {
        voiceHotkeyActiveRef.current = true;
        clearArmTimer();
        armTimer = setTimeout(() => {
          armTimer = null;
          if (voiceHotkeyActiveRef.current && voiceStatusRef.current === "idle") {
            composerRef.current?.voiceStart();
          }
        }, PUSH_TO_TALK_ARM_DELAY_MS);
        return;
      }

      if (
        isModified(event) &&
        String(event.key).toLowerCase() === "k" &&
        isPC &&
        isTabVisible() &&
        !renameConversationRef.current &&
        !sourcesOpenRef.current &&
        !isEditableTarget(event)
      ) {
        event.preventDefault();
        sidebarSearchRef.current?.focus();
      }
    };

    const onKeyUp = (event) => {
      if (!voiceHotkeyActiveRef.current) return;
      if (event.key === "Shift" || event.key === "Meta" || event.key === "Control") {
        voiceHotkeyActiveRef.current = false;
        clearArmTimer();
        composerRef.current?.voiceStop();
      }
    };

    // Capture phase is required: React 16's delegated root listener runs in
    // bubble phase at the document and synchronously commits drawer/modal
    // closes (setSourcesOpen(null) + ref updates) before a bubble-phase
    // document handler runs — the guards above would then read post-close
    // state and one Esc would both close the drawer AND stop the stream.
    // Capture fires before the rc-drawer/rc-dialog handlers, so the refs
    // still reflect the at-keydown world and the drawer's own Esc wins.
    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("keyup", onKeyUp);
    return () => {
      clearArmTimer();
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("keyup", onKeyUp);
    };
    /* No voice.* identities in the deps: the recorder is reached through
       composerRef at event time (the panel's imperative methods are
       identity-stable), and isStreaming re-subscribes exactly as before so
       the closure guards read fresh values. */
  }, [isStreaming, isPC, handleStop]);

  const selectedConversation = useMemo(
    () => conversations.find((conversation) => conversation.id === selectedConversationId) || null,
    [conversations, selectedConversationId]
  );

  const visibleConversations = useMemo(() => {
    const keyword = sidebarSearch.trim().toLowerCase();
    if (!keyword) return conversations;
    return conversations.filter((conversation) =>
      (conversation.title || "").toLowerCase().includes(keyword)
    );
  }, [conversations, sidebarSearch]);

  /* Sidebar date groups (2.2): flat while searching, otherwise bucketed by
     day. Buckets are created on demand over the desc-sorted list, so order
     follows sortConversations (pinned first, then newest activity). */
  const conversationGroups = useMemo(
    () =>
      groupConversationsByDay(visibleConversations, {
        flat: Boolean(sidebarSearch.trim()),
      }),
    [visibleConversations, sidebarSearch]
  );

  /* Mobile header Select options — antd 4 grouped format (2.2). Built from
     the unfiltered list: the sidebar search is PC-only state. */
  const conversationOptions = useMemo(
    () => buildConversationSelectOptions(conversations),
    [conversations]
  );

  const upsertConversation = useCallback((conversation) => {
    if (!conversation?.id) return;

    setConversations((current) => {
      const next = [conversation, ...current.filter((item) => item.id !== conversation.id)];
      return sortConversations(next);
    });
  }, []);

  const updateAttachment = useCallback((localId, patch) => {
    setAttachments((current) =>
      current.map((item) => (item.localId === localId ? { ...item, ...patch } : item))
    );
  }, []);

  const handleAttachmentFiles = useCallback(
    (fileList) => {
      const files = Array.from(fileList || []).filter(Boolean);
      if (!files.length || !companyId || !facilityId) return;

      files.forEach((file) => {
        const name = file.name || "attachment";
        const extension = `.${name.split(".").pop() || ""}`.toLowerCase();
        if (!ATTACHMENT_EXTENSIONS.has(extension)) {
          antdMessage.error(`${name}: this file type is not supported.`);
          return;
        }
        if (file.size > ATTACHMENT_MAX_BYTES) {
          antdMessage.error(`${name}: files up to 20 MB are supported.`);
          return;
        }

        attachmentSeqRef.current += 1;
        const localId = `att-${Date.now()}-${attachmentSeqRef.current}`;
        setAttachments((current) => [
          ...current,
          { localId, name, size: file.size, status: "uploading" },
        ]);

        uploadFacilityAiAttachment({
          companyId,
          facilityId,
          conversationId: selectedConversationIdRef.current,
          file,
        })
          .then((payload) => {
            if (payload?.conversation && !selectedConversationIdRef.current) {
              selectedConversationIdRef.current = payload.conversation.id;
              setSelectedConversationId(payload.conversation.id);
              upsertConversation(payload.conversation);
            }
            updateAttachment(localId, {
              status: "ready",
              attachmentId: payload.id,
              mediaType: payload.media_type,
              preview: payload.preview,
            });
          })
          .catch((error) => {
            updateAttachment(localId, {
              status: "failed",
              error: error?.message || "The file could not be processed.",
            });
          });
      });
    },
    [companyId, facilityId, updateAttachment, upsertConversation]
  );

  const removeAttachment = useCallback((localId) => {
    setAttachments((current) => current.filter((item) => item.localId !== localId));
  }, []);

  const loadConversations = useCallback(async () => {
    if (!companyId || !facilityId) return;

    setConversationsLoading(true);
    try {
      const payload = await listFacilityAiConversations(companyId, facilityId);
      // Server order is not guaranteed desc; the day grouping relies on
      // the sortConversations order (pinned first, then newest activity).
      // The post-stream reload goes through this same function.
      setConversations(sortConversations(getCollectionItems(payload)));
    } catch (error) {
      antdMessage.error(error?.message || "Unable to load AI conversations.");
    } finally {
      setConversationsLoading(false);
    }
  }, [companyId, facilityId]);

  const loadMessages = useCallback(
    async (conversationId) => {
      if (!conversationId || !companyId || !facilityId) return;

      setMessagesLoading(true);
      try {
        const payload = await getFacilityAiConversationMessages(companyId, facilityId, conversationId);
        setMessages(getCollectionItems(payload).map(normalizeMessage));
      } catch (error) {
        antdMessage.error(error?.message || "Unable to load this conversation.");
      } finally {
        setMessagesLoading(false);
      }
    },
    [companyId, facilityId]
  );

  useEffect(() => {
    const scopeKey = `${companyId || ""}:${facilityId || ""}`;
    scopeRef.current = scopeKey;
    streamControllerRef.current?.abort();
    cancelStreamFrame();
    streamContentRef.current = { messageId: null, content: "" };
    followOutputRef.current = true;
    setShowScrollToBottom(false);
    setSelectedConversationId(null);
    setMessages([]);
    setAttachments([]);
    setConversationAttachments([]);
    setWebSearch("auto");
    setSourcesOpen(null);
    // Programmatic draft reset with the skip marker (facility switch) — the
    // marker + persistence cycle now live inside ComposerPanel.clearDraft().
    // Child effects run before this one on the facility-change commit, so a
    // pending debounced save was already flushed under the OLD key; the
    // wipe below removes it, leaving storage exactly as the old
    // discard-then-wipe ordering did.
    composerRef.current?.clearDraft();
    // Leaving a facility drops its stored drafts so storage never leaks
    // across facilities (guarded so the first mount keeps today's drafts).
    if (lastFacilityIdRef.current && lastFacilityIdRef.current !== facilityId) {
      clearFacilityDrafts(userId, lastFacilityIdRef.current);
    }
    lastFacilityIdRef.current = facilityId || null;
    loadConversations();

    return () => {
      streamControllerRef.current?.abort();
    };
  }, [companyId, facilityId, userId, loadConversations, cancelStreamFrame]);

  useEffect(() => {
    const previousMessages = prevMessagesRef.current;
    const messagesChanged = previousMessages !== messages;
    if (messagesChanged) prevMessagesRef.current = messages;

    if (!followOutputRef.current) {
      if (messages.length) setShowScrollToBottom(true);
      if (!messagesChanged) return;
      if (
        messages.length > previousMessages.length &&
        previousMessages.length > 0 &&
        messages[previousMessages.length - 1] === previousMessages[previousMessages.length - 1]
      ) {
        // New rows appended while scrolled away (3.9) → count only the
        // assistant rows (the local user echo is the submitter's own
        // message, so it must not inflate the badge).
        const newAssistantRows = messages
          .slice(previousMessages.length)
          .filter((item) => item.role === "assistant").length;
        if (newAssistantRows > 0) {
          unreadCountRef.current += newAssistantRows;
          setUnreadCount(unreadCountRef.current);
        }
      } else if (messages.length && messages.length === previousMessages.length) {
        // Same length, changed content (streaming deltas) → dot, not a count.
        streamedWhileAwayRef.current = true;
        setStreamedWhileAway(true);
      } else if (messages.length < previousMessages.length) {
        // Cleared/replaced (conversation switch/delete): never carry unread
        // state across conversations.
        resetUnreadIndicators();
      }
      return;
    }
    // Fresh load (empty → populated): every row starts as a ~160px
    // content-visibility placeholder, so scrollHeight is wrong until the
    // bottom rows paint — take the instant path so scrollToLatest's
    // second-frame re-anchor lands at the true bottom. Non-streaming
    // updates with existing rows (answer finished) keep the smooth
    // animation; those rows are already at real heights.
    scrollToLatest(
      isStreaming || (messagesChanged && previousMessages.length === 0) ? "auto" : "smooth"
    );
  }, [messages, isStreaming, scrollToLatest, resetUnreadIndicators]);

  useEffect(() => {
    return () => {
      streamControllerRef.current?.abort();
      cancelStreamFrame();
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    };
  }, [cancelStreamFrame]);

  /* Global FAB clearance (P0 fix 6): while the assistant page is mounted,
     mark the body so styles.css can lift the app-level Agent launcher
     stack above the mobile composer (media-gated to ≤767px in CSS, so
     desktop — where keep-alive keeps this page mounted across tab
     switches — is unaffected; mobile keep-alive is off, so unmount always
     removes the class). */
  useEffect(() => {
    if (embedded) return undefined;
    document.body.classList.add("facility-ai-active");
    return () => document.body.classList.remove("facility-ai-active");
  }, [embedded]);

  const handleMessagesScroll = useCallback(
    (event) => {
      const node = event.currentTarget;
      const isNearBottom = node.scrollHeight - node.scrollTop - node.clientHeight < 96;
      followOutputRef.current = isNearBottom;
      setShowScrollToBottom(!isNearBottom);
      // Follow mode re-engaged by scrolling back to the bottom (3.9).
      if (isNearBottom) resetUnreadIndicators();
    },
    [resetUnreadIndicators]
  );

  /* Stable identity for the memoized ConversationRow (3.10): reads
     isStreaming / selectedConversationId through their refs so the
     callback never changes (and rows never re-render) across stream
     frames. */
  const handleSelectConversation = useCallback(
    async (conversationId) => {
      if (isStreamingRef.current || conversationId === selectedConversationIdRef.current) return;

      setSelectedConversationId(conversationId);
      setMessages([]);
      setAttachments([]);
      setConversationAttachments([]);
      await Promise.all([
        loadMessages(conversationId),
        getFacilityAiConversation(companyId, facilityId, conversationId)
          .then((payload) => {
            setConversationAttachments(
              Array.isArray(payload?.attachments) ? payload.attachments : []
            );
          })
          .catch(() => setConversationAttachments([])),
      ]);
      // Restore this conversation's draft once the switch settles; skip while
      // streaming so a late load never clobbers an in-flight prompt.
      if (!isStreamingRef.current) {
        composerRef.current?.setDraft(readStoredDraft(userId, facilityId, conversationId));
      } else {
        // Restore was skipped: the old draft must not linger in the composer
        // while the persist scope already points at the new conversation (the
        // next keystroke would save A's text under B's key). Clear it without
        // scheduling a save, via the reset-marker clearDraft().
        composerRef.current?.clearDraft();
      }
    },
    [companyId, facilityId, userId, loadMessages]
  );

  const handleNewConversation = () => {
    if (isStreaming) return;
    setSelectedConversationId(null);
    setMessages([]);
    composerRef.current?.setDraft(readStoredDraft(userId, facilityId, null));
    setAttachments([]);
    setConversationAttachments([]);
    requestAnimationFrame(() => composerRef.current?.focus());
  };

  const handleRename = async () => {
    const title = renameValue.trim();
    if (!renameConversation || !title) {
      antdMessage.error("Enter a conversation name.");
      return;
    }

    setRenameSaving(true);
    try {
      const updated = await updateFacilityAiConversation(
        companyId,
        facilityId,
        renameConversation.id,
        { title }
      );
      upsertConversation(updated);
      setRenameConversation(null);
    } catch (error) {
      antdMessage.error(error?.message || "Unable to rename the conversation.");
    } finally {
      setRenameSaving(false);
    }
  };

  /* Stable identity for the memoized ConversationRow (3.10). */
  const handleDelete = useCallback(
    (conversation) => {
      Modal.confirm({
        title: "Delete conversation?",
        content: "This removes the conversation and its AI context for this facility.",
        okText: "Delete",
        okType: "danger",
        cancelText: "Cancel",
        onOk: async () => {
          try {
            await deleteFacilityAiConversation(companyId, facilityId, conversation.id);
            setConversations((current) => current.filter((item) => item.id !== conversation.id));
            if (selectedConversationIdRef.current === conversation.id) {
              setSelectedConversationId(null);
              setMessages([]);
            }
          } catch (error) {
            antdMessage.error(error?.message || "Unable to delete the conversation.");
            throw error;
          }
        },
      });
    },
    [companyId, facilityId]
  );

  /* Stable identity for the memoized ConversationRow (3.10). */
  const handleTogglePin = useCallback(
    async (conversation) => {
      try {
        const updated = await updateFacilityAiConversation(
          companyId,
          facilityId,
          conversation.id,
          { is_pinned: !conversation.is_pinned }
        );
        upsertConversation(updated);
      } catch (error) {
        antdMessage.error(
          error?.message ||
            `Unable to ${conversation.is_pinned ? "unpin" : "pin"} the conversation.`
        );
      }
    },
    [companyId, facilityId, upsertConversation]
  );

  const updateStreamingMessage = useCallback((localMessageId, patch) => {
    setMessages((current) =>
      current.map((item) => (item.id === localMessageId ? { ...item, ...patch } : item))
    );
  }, []);

  const settleBufferedAnswer = useCallback(
    (localMessageId, finalContent = "") => {
      cancelStreamFrame();
      const streamedContent =
        streamContentRef.current.messageId === localMessageId
          ? streamContentRef.current.content
          : "";
      const content = mergeAnswerContent(streamedContent, finalContent);
      streamContentRef.current = { messageId: localMessageId, content };
      return content;
    },
    [cancelStreamFrame]
  );

  const appendProgressSnapshot = useCallback((localMessageId, eventPayload) => {
    const trace = normalizeProgressTrace(eventPayload);
    setMessages((current) =>
      current.map((item) => {
        if (item.id !== localMessageId) return item;
        return {
          ...item,
          activity: { trace },
        };
      })
    );
  }, []);

  const settleStreamingActivity = useCallback((localMessageId, status) => {
    setMessages((current) =>
      current.map((item) =>
        item.id === localMessageId
          ? { ...item, activity: settleActivity(item.activity, status) }
          : item
      )
    );
  }, []);

  const appendAnswerDelta = useCallback((localMessageId, delta) => {
    if (delta === undefined || delta === null || delta === "") return;
    const nextDelta = typeof delta === "string" ? delta : String(delta);
    if (streamContentRef.current.messageId !== localMessageId) {
      streamContentRef.current = { messageId: localMessageId, content: "" };
    }
    streamContentRef.current.content += nextDelta;
    if (streamFrameRef.current !== null) return;

    streamFrameRef.current = requestAnimationFrame(() => {
      streamFrameRef.current = null;
      const snapshot = streamContentRef.current;
      if (snapshot.messageId !== localMessageId) return;
      updateStreamingMessage(localMessageId, {
        content_markdown: snapshot.content,
      });
    });
  }, [updateStreamingMessage]);

  const appendWebResults = useCallback((localMessageId, webResults) => {
    if (!Array.isArray(webResults) || !webResults.length) return;

    const normalized = webResults
      .filter((result) => result && typeof result.url === "string" && result.url)
      .map((result) => ({
        title: typeof result.title === "string" ? result.title : result.url,
        url: result.url,
        content: typeof result.content === "string" ? result.content : "",
      }));

    setMessages((current) =>
      current.map((item) => {
        if (item.id !== localMessageId) return item;
        const merged = [...(item.web_results || []), ...normalized];
        const seen = new Set();
        const unique = merged.filter((result) => {
          if (seen.has(result.url)) return false;
          seen.add(result.url);
          return true;
        });
        return { ...item, web_results: unique.slice(0, 10) };
      })
    );
  }, []);

  const appendWebImages = useCallback((localMessageId, webImages) => {
    if (!Array.isArray(webImages) || !webImages.length) return;

    const normalized = webImages
      .filter(
        (image) =>
          image &&
          typeof image.url === "string" &&
          /^https?:\/\//i.test(image.url)
      )
      .map((image) => ({
        url: image.url,
        description:
          typeof image.description === "string" ? image.description : "",
      }));

    if (!normalized.length) return;

    setMessages((current) =>
      current.map((item) => {
        if (item.id !== localMessageId) return item;
        const merged = [...(item.web_images || []), ...normalized];
        const seen = new Set();
        const unique = merged.filter((image) => {
          if (seen.has(image.url)) return false;
          seen.add(image.url);
          return true;
        });
        return { ...item, web_images: unique.slice(0, 8) };
      })
    );
  }, []);

  const submitPrompt = async (prompt, { clearDraft = true, resubmitMessageId = null } = {}) => {
    const content = String(prompt || "").trim();
    if (!content || isStreaming || !companyId || !facilityId) return;
    if (attachmentsUploading) {
      antdMessage.info("Please wait for the attachments to finish processing.");
      return;
    }

    const resubmit = Boolean(resubmitMessageId);
    let resubmitServerMessageId;
    let resubmitTargetSnapshot = null;
    if (resubmit) {
      const targetIndex = messages.findIndex(
        (item) => item.id === resubmitMessageId && item.role === "assistant"
      );
      if (targetIndex < 0) return;
      let lastAssistantIndex = -1;
      for (let index = messages.length - 1; index >= 0; index -= 1) {
        if (messages[index].role === "assistant") {
          lastAssistantIndex = index;
          break;
        }
      }
      if (lastAssistantIndex !== targetIndex) return;
      const targetMessage = messages[targetIndex];
      if (targetMessage.status === "streaming") return;
      resubmitTargetSnapshot = targetMessage;
      const candidateServerId = targetMessage.server_id ?? targetMessage.id;
      if (typeof candidateServerId === "number") {
        resubmitServerMessageId = candidateServerId;
      }
    }

    const requestId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const localUserMessageId = `local-user-${requestId}`;
    const localAssistantMessageId = `local-assistant-${requestId}`;
    const targetAssistantId = resubmitMessageId || localAssistantMessageId;
    const controller = new AbortController();
    const requestScope = `${companyId}:${facilityId}`;
    let streamConversationId = selectedConversationId;

    streamControllerRef.current = controller;
    streamContentRef.current = { messageId: targetAssistantId, content: "" };
    // After-send clear (3.3): plain setDraft("") — deliberately NOT the
    // reset-marker clearDraft() — so ComposerPanel's persistence effect
    // still schedules its write and writeStoredDraft removes the key,
    // exactly like the old direct setDraft("") after a successful start.
    if (clearDraft) composerRef.current?.setDraft("");
    setIsStreaming(true);
    scrollToLatest("auto", true);
    if (resubmit) {
      setMessages((current) =>
        current.map((item) =>
          item.id === targetAssistantId
            ? {
                ...item,
                content_markdown: "",
                status: "streaming",
                activity: createInitialActivity(),
                web_results: [],
                web_images: [],
                response_manifest: null,
                error_message: null,
              }
            : item
        )
      );
    } else {
      setMessages((current) => [
        ...current,
        {
          id: localUserMessageId,
          role: "user",
          content_markdown: content,
          status: "done",
          created_at: new Date().toISOString(),
        },
        {
          id: localAssistantMessageId,
          role: "assistant",
          content_markdown: "",
          status: "streaming",
          activity: createInitialActivity(),
          created_at: new Date().toISOString(),
        },
      ]);
    }

    try {
      await streamFacilityAiChat({
        companyId,
        facilityId,
        conversationId: selectedConversationId,
        message: content,
        webSearch,
        reasoningEffort,
        pageContext: currentPageEnabled ? currentPageContext : null,
        resubmit,
        resubmitMessageId: resubmit ? resubmitServerMessageId : undefined,
        signal: controller.signal,
        onEvent: (eventPayload) => withBatch(() => {
          if (scopeRef.current !== requestScope) return;

          if (eventPayload.event === "start" && typeof eventPayload.message_id === "number") {
            updateStreamingMessage(targetAssistantId, {
              server_id: eventPayload.message_id,
            });
            return;
          }

          if (eventPayload.event === "conversation_created") {
            streamConversationId = eventPayload.conversation_id;
            setSelectedConversationId(eventPayload.conversation_id);
            upsertConversation(eventPayload.conversation);
            return;
          }

          if (eventPayload.event === "answer_reset") {
            cancelStreamFrame();
            streamContentRef.current = {
              messageId: targetAssistantId,
              content: "",
            };
            updateStreamingMessage(targetAssistantId, {
              content_markdown: "",
            });
            return;
          }

          if (eventPayload.event === "answer_delta" || eventPayload.event === "delta") {
            appendAnswerDelta(
              targetAssistantId,
              eventPayload.delta ?? eventPayload.token ?? eventPayload.text ?? eventPayload.content
            );
            return;
          }

          if (eventPayload.event === "progress") {
            if (Array.isArray(eventPayload.web_results)) {
              appendWebResults(targetAssistantId, eventPayload.web_results);
            }
            if (Array.isArray(eventPayload.web_images)) {
              appendWebImages(targetAssistantId, eventPayload.web_images);
            }
            appendProgressSnapshot(targetAssistantId, eventPayload);
            return;
          }

          if (eventPayload.event === "tool_result") {
            if (Array.isArray(eventPayload.web_results)) {
              appendWebResults(targetAssistantId, eventPayload.web_results);
            }
            if (Array.isArray(eventPayload.web_images)) {
              appendWebImages(targetAssistantId, eventPayload.web_images);
            }
          }

          if (eventPayload.event === "answer_done") {
            const settledContent = settleBufferedAnswer(
              targetAssistantId,
              typeof eventPayload.answer === "string" ? eventPayload.answer : ""
            );
            setMessages((current) =>
              current.map((item) => {
                if (item.id !== targetAssistantId) return item;
                return {
                  ...item,
                  content_markdown: settledContent,
                  status: eventPayload.error ? "error" : "done",
                  response_manifest: normalizeResponseManifest(
                    eventPayload.response_manifest
                  ),
                  activity: settleActivity(
                    item.activity,
                    eventPayload.error ? "failed" : "completed"
                  ),
                };
              })
            );
            return;
          }

          if (eventPayload.event === "done") {
            const serverMessage = eventPayload.message
              ? normalizeMessage(eventPayload.message)
              : {};
            const finalContent =
              serverMessage.content_markdown || eventPayload.answer || "";
            const settledContent = settleBufferedAnswer(
              targetAssistantId,
              finalContent
            );
            setMessages((current) =>
              current.map((item) => {
                if (item.id !== targetAssistantId) return item;
                const finalStatus = serverMessage.status || "done";
                return {
                  ...item,
                  ...serverMessage,
                  id: targetAssistantId,
                  content_markdown: settledContent,
                  status: finalStatus,
                  activity: settleActivity(
                    serverMessage.activity?.trace?.steps?.length
                      ? serverMessage.activity
                      : item.activity,
                    finalStatus === "error" ? "failed" : "completed"
                  ),
                };
              })
            );
            return;
          }

          if (eventPayload.event === "error") {
            if (eventPayload.code === "service_error" || !eventPayload.code) {
              const detail =
                eventPayload.detail ||
                eventPayload.message ||
                "The AI response could not be completed.";
              const streamedContent = settleBufferedAnswer(targetAssistantId);
              updateStreamingMessage(targetAssistantId, {
                content_markdown: streamedContent || detail,
                error_message: streamedContent ? detail : "",
                status: "error",
              });
            }
            settleStreamingActivity(targetAssistantId, "failed");
          }
        }),
      });
    } catch (error) {
      const interrupted = error?.name === "AbortError";
      const generationPending =
        !interrupted && /already being generated/i.test(String(error?.message || ""));
      if (resubmit && generationPending) {
        antdMessage.info(
          "The previous response is still finishing. Please try again in a moment."
        );
        setMessages((current) =>
          current.map((item) =>
            item.id === targetAssistantId ? resubmitTargetSnapshot : item
          )
        );
      } else {
        const streamedContent = settleBufferedAnswer(targetAssistantId);
        const detail = interrupted
          ? "The response was stopped."
          : error?.message || "The AI response could not be completed.";
        updateStreamingMessage(targetAssistantId, {
          content_markdown: streamedContent || detail,
          error_message: streamedContent ? detail : "",
          status: interrupted ? "stopped" : "error",
        });
        settleStreamingActivity(targetAssistantId, interrupted ? "stopped" : "failed");
        if (!interrupted) {
          antdMessage.error(error?.message || "Unable to reach the AI service.");
        }
      }
    } finally {
      // Batched (3.2): the stream teardown fires 2-4 setStates — one render.
      withBatch(() => {
        if (streamControllerRef.current === controller) {
          streamControllerRef.current = null;
        }
        setIsStreaming(false);
        const bufferedContent = settleBufferedAnswer(targetAssistantId);
        setMessages((current) =>
          current.map((item) => {
            if (item.id !== targetAssistantId || item.status !== "streaming") return item;
            const activity = settleActivity(item.activity, "failed");
            return {
              ...item,
              status: "error",
              content_markdown:
                bufferedContent ||
                item.content_markdown ||
                "The AI response could not be completed.",
              error_message: bufferedContent
                ? "The AI response could not be completed."
                : item.error_message,
              activity,
            };
          })
        );
        streamContentRef.current = { messageId: null, content: "" };
        if (!resubmit) {
          const sentAttachments = attachmentsRef.current.filter(
            (item) => item.status === "ready"
          );
          if (sentAttachments.length) {
            setAttachments([]);
            if (streamConversationId) {
              setConversationAttachments((existing) => [
                ...existing,
                ...sentAttachments.map((item) => ({
                  id: item.attachmentId,
                  original_filename: item.name,
                  media_type: item.mediaType || "",
                  parse_status: "ready",
                  preview: item.preview || "",
                })),
              ]);
            }
          }
        }
        if (streamConversationId && scopeRef.current === requestScope) {
          loadConversations();
        }
      });
    }
  };

  /* Latest submitPrompt for stable handlers (Wave 3.1): submitPrompt closes
     over lots of render state, so a ref lets handleRegenerateMessage stay
     referentially stable without re-creating it every render. */
  const submitPromptRef = useRef(submitPrompt);
  submitPromptRef.current = submitPrompt;

  /* ComposerPanel submit entry (3.3): the panel owns the draft and passes
     its text here; submitPrompt's clearDraft option still runs at the exact
     same point inside submitPrompt (after the guards), now through the
     composer ref. Stable identity so the memoized panel never re-renders
     from this prop. */
  const handleSubmitFromComposer = useCallback((text) => {
    return submitPromptRef.current(text);
  }, []);

  /* Stable identity for the memoized MessageItem rows (Wave 3.1): only uses
     module-level copyText, a stable state setter and a timer ref, so empty
     deps are correct and behavior is unchanged (values read at click time). */
  const handleCopyMessage = useCallback(async (item) => {
    try {
      await copyText(item.content_markdown);
      setCopiedMessageId(item.id);
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = setTimeout(() => setCopiedMessageId(null), 1600);
    } catch (error) {
      antdMessage.error(error?.message || "Unable to copy this answer.");
    }
  }, []);

  /* Stable identity for the memoized MessageItem rows (Wave 3.1): reads
     messages/submitPrompt through refs so clicks always see the latest
     values while the callback identity never changes. */
  const handleRegenerateMessage = useCallback((messageId) => {
    const currentMessages = messagesRef.current;
    const messageIndex = currentMessages.findIndex(
      (item) => item.id === messageId && item.role === "assistant"
    );
    if (messageIndex < 0) return;
    const previousUserMessage = currentMessages
      .slice(0, messageIndex)
      .reverse()
      .find((item) => item.role === "user" && item.content_markdown);
    if (!previousUserMessage) return;
    submitPromptRef.current(previousUserMessage.content_markdown, {
      clearDraft: false,
      resubmitMessageId: messageId,
    });
  }, []);

  /* Stable identity for the memoized MessageItem rows (Wave 3.1):
     setSourcesOpen is a stable state setter, so this never changes. */
  const handleOpenSources = useCallback((messageId, citationId) => {
    setSourcesOpen({ messageId, citationId });
  }, []);

  /* Conversation menu dispatch (3.10): one stable handler shared by the
     memoized ConversationRow (via its onMenuClick prop) and the mobile
     header dropdown below. */
  const handleConversationMenuClick = useCallback(
    (conversation, key) => {
      if (key === "pin") {
        handleTogglePin(conversation);
      } else if (key === "rename") {
        setRenameConversation(conversation);
        setRenameValue(conversation.title || "");
      } else if (key === "delete") {
        handleDelete(conversation);
      }
    },
    [handleTogglePin, handleDelete]
  );

  const buildConversationMenu = (conversation) => ({
    items: conversationMenuItems(Boolean(conversation?.is_pinned)),
    onClick: ({ key }) => handleConversationMenuClick(conversation, key),
  });

  /* ComposerPanel wiring (3.3): option states stay HERE because
     submitPrompt reads them at send time and they change on rare clicks,
     not per keystroke. Every callback below is identity-stable, and the
     current-page context is decomposed into primitives (title string /
     chart count) so the memoized panel's comparator never sees a fresh
     object per render. */
  const handleToggleWebSearch = useCallback(() => {
    setWebSearch((current) => (current === "auto" ? "off" : "auto"));
  }, []);
  const handleToggleCurrentPage = useCallback(() => {
    setCurrentPageEnabled((current) => !current);
  }, []);
  const currentPageTitle = currentPageContext?.page_title || "";
  const currentPageChartCount = currentPageContext?.charts?.length || 0;

  let lastAssistantMessageId = null;
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (messages[index].role === "assistant") {
      lastAssistantMessageId = messages[index].id;
      break;
    }
  }

  return (
    <div
      className={`facility-ai-page${embedded ? " facility-ai-page--popup" : ""}`}
      ref={pageRootRef}
    >
      <aside className="facility-ai-sidebar" aria-label="AI conversations">
        <div className="facility-ai-sidebar-header">
          <div>
            <div className="facility-ai-sidebar-eyebrow">Facility</div>
            <h1>AI Assistant</h1>
          </div>
        </div>

        <div className="facility-ai-sidebar-new">
          <Tooltip title="New conversation">
            <div
              type="button"
              className="facility-ai-new-conversation"
              onClick={handleNewConversation}
              disabled={isStreaming}
              aria-label="New conversation"
            >
              <PlusOutlined aria-hidden="true" />
              <span>New conversation</span>
            </div>
          </Tooltip>
        </div>

        <div className="facility-ai-sidebar-search">
          <Input
            ref={sidebarSearchRef}
            allowClear
            prefix={<SearchOutlined />}
            value={sidebarSearch}
            onChange={(event) => setSidebarSearch(event.target.value)}
            placeholder="Search conversations (⌘K)"
          />
        </div>

        <div className="facility-ai-conversation-list">
          {conversationsLoading ? (
            <div className="facility-ai-list-loading"><Spin size="small" /></div>
          ) : visibleConversations.length === 0 ? (
            <div className="facility-ai-list-empty">
              {sidebarSearch.trim() ? "No results found." : "No conversations yet."}
            </div>
          ) : (
            conversationGroups.map((group) => (
              <React.Fragment key={group.key}>
                {group.label ? (
                  <div className="facility-ai-list-label">{group.label}</div>
                ) : null}
                {group.items.map((conversation) => (
                  <ConversationRow
                    key={conversation.id}
                    conversation={conversation}
                    isActive={conversation.id === selectedConversationId}
                    disabled={isStreaming}
                    onSelect={handleSelectConversation}
                    onMenuClick={handleConversationMenuClick}
                  />
                ))}
              </React.Fragment>
            ))
          )}
        </div>
      </aside>

      <main className="facility-ai-main">
        <header className="facility-ai-main-header">
          {isPC ? (
            <div className="facility-ai-heading">
              <span className="facility-ai-heading-icon" aria-hidden="true">
                <RobotOutlined />
              </span>
              <div className="facility-ai-heading-text">
                <h2>{selectedConversation?.title || "New conversation"}</h2>
                <span className="facility-ai-scope-badge">
                  <AimOutlined aria-hidden="true" />
                  Answers are scoped to this facility
                </span>
              </div>
            </div>
          ) : (
            <div className="facility-ai-h5-header-group">
              <div className="facility-ai-h5-header">
                <Select
                  showSearch
                  value={selectedConversationId ?? undefined}
                  placeholder="Select conversation"
                  options={conversationOptions}
                  loading={conversationsLoading}
                  disabled={isStreaming}
                  onChange={(value) => {
                    if (value) handleSelectConversation(value);
                  }}
                />
                <div className="facility-ai-h5-header-actions">
                  <Dropdown
                    menu={buildConversationMenu(selectedConversation)}
                    trigger={["click"]}
                    placement="bottomRight"
                  >
                    <Button
                      type="text"
                      icon={<MoreOutlined />}
                      aria-label="Conversation actions"
                      disabled={!selectedConversation || isStreaming}
                    />
                  </Dropdown>
                  <Button
                    type="text"
                    icon={<PlusOutlined />}
                    aria-label="New conversation"
                    onClick={handleNewConversation}
                    disabled={isStreaming}
                  />
                </div>
              </div>
              <span className="facility-ai-scope-badge facility-ai-scope-badge--compact">
                <AimOutlined aria-hidden="true" />
                Answers are scoped to this facility
              </span>
            </div>
          )}
        </header>

        <section
          ref={messagesContainerRef}
          className="facility-ai-messages"
          onScroll={handleMessagesScroll}
        >
          {conversationAttachments.length ? (
            <AttachmentList aria-label="Conversation files" style={{ padding: "4px 2px 0" }}>
              {conversationAttachments.map((item) => (
                <Tooltip
                  key={item.id}
                  title={
                    item.parse_status === "ready"
                      ? item.preview || item.original_filename
                      : item.error_message || "This file could not be processed."
                  }
                >
                  <AttachmentChip $failed={item.parse_status === "failed"}>
                    <FileOutlined aria-hidden="true" />
                    <span className="facility-ai-attachment-name">{item.original_filename}</span>
                  </AttachmentChip>
                </Tooltip>
              ))}
            </AttachmentList>
          ) : null}
          {messagesLoading ? (
            <div
              className="facility-ai-messages-loading"
              role="status"
              aria-label="Loading conversation"
            >
              <span className="facility-ai-skeleton-bar" />
              <span className="facility-ai-skeleton-bar" />
              <span className="facility-ai-skeleton-bar" />
            </div>
          ) : messages.length === 0 ? (
            <div className="facility-ai-empty-state">
              <div className="facility-ai-empty-icon"><RobotOutlined /></div>
              <h2>How can I help with this facility?</h2>
              <p>Ask about energy, equipment, work orders, or FacilityKPI product guidance.</p>
              <div className="facility-ai-suggestion-grid">
                {SUGGESTIONS.map(({ key, title, description, prompt, icon: Icon }) => (
                  <button
                    type="button"
                    key={key}
                    className="facility-ai-suggestion-card"
                    onClick={() => {
                      if (!composerRef.current?.getDraft().trim()) {
                        composerRef.current?.setDraft(prompt);
                      }
                      requestAnimationFrame(() => composerRef.current?.focus());
                    }}
                    disabled={isStreaming}
                  >
                    <span className="facility-ai-suggestion-icon" aria-hidden="true">
                      <Icon />
                    </span>
                    <span className="facility-ai-suggestion-copy">
                      <span className="facility-ai-suggestion-title">{title}</span>
                      <span className="facility-ai-suggestion-description">{description}</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((item, messageIndex) => {
              const currentDayKey = item.created_at ? calendarDayKey(item.created_at) : null;
              const previousDayKey =
                messageIndex > 0 ? calendarDayKey(messages[messageIndex - 1].created_at) : null;
              // Day separator (2.3): rendered when the calendar day changes.
              // The first message of a conversation started today gets none.
              const separatorLabel =
                currentDayKey && currentDayKey !== previousDayKey
                  ? formatDaySeparator(item.created_at)
                  : "";
              const showSeparator =
                Boolean(separatorLabel) && !(messageIndex === 0 && separatorLabel === "Today");
              return (
                <React.Fragment key={item.id}>
                  {showSeparator ? (
                    <div className="facility-ai-day-separator">
                      <span>{separatorLabel}</span>
                    </div>
                  ) : null}
                  <MessageItem
                    item={item}
                    isLast={messageIndex === messages.length - 1}
                    isLastAssistant={item.id === lastAssistantMessageId}
                    isStreaming={isStreaming}
                    copiedMessageId={copiedMessageId}
                    onCopyMessage={handleCopyMessage}
                    onRegenerateMessage={handleRegenerateMessage}
                    onOpenSources={handleOpenSources}
                  />
                </React.Fragment>
              );
            })
          )}
        </section>
        {showScrollToBottom ? (
          <Tooltip title="Scroll to latest">
            <button
              type="button"
              className="facility-ai-scroll-latest"
              onClick={() => scrollToLatest("smooth", true)}
              aria-label={
                unreadCount > 0
                  ? `Scroll to latest message (${unreadCount} new)`
                  : "Scroll to latest message"
              }
            >
              <DownOutlined />
              {/* Unread badge (3.9): pill counts new rows that arrived while
                  scrolled away; dot (pulsing only while streaming) marks
                  same-message streaming growth. Both clear when follow mode
                  re-engages. */}
              {unreadCount > 0 ? (
                <span className="facility-ai-scroll-latest-badge" aria-hidden="true">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              ) : streamedWhileAway ? (
                <span
                  className={`facility-ai-scroll-latest-dot${isStreaming ? " is-pulsing" : ""}`}
                  aria-hidden="true"
                />
              ) : null}
            </button>
          </Tooltip>
        ) : null}

        {/* Composer island (3.3): memoized — a keystroke updates draft state
            INSIDE ComposerPanel and re-renders nothing else on this page;
            the recorder's 5 Hz timer ticks are contained there too. Draft
            persistence, paste-to-attach, Enter-to-send and the voice pill
            all live in the panel; this page talks to it only through the
            stable props below and the composerRef imperative handle. */}
        <ComposerPanel
          ref={composerRef}
          isStreaming={isStreaming}
          webSearch={webSearch}
          onToggleWebSearch={handleToggleWebSearch}
          currentPageEnabled={currentPageEnabled}
          currentPageAvailable={Boolean(currentPageContext)}
          currentPageTitle={currentPageTitle}
          currentPageChartCount={currentPageChartCount}
          onToggleCurrentPage={handleToggleCurrentPage}
          reasoningEffort={reasoningEffort}
          onChangeReasoningEffort={setReasoningEffort}
          attachments={attachments}
          attachmentsUploading={attachmentsUploading}
          onRemoveAttachment={removeAttachment}
          onAttachFiles={handleAttachmentFiles}
          onSubmit={handleSubmitFromComposer}
          onStop={handleStop}
          onVoiceStatusChange={handleVoiceStatusChange}
          companyId={companyId}
          facilityId={facilityId}
          userId={userId}
          conversationId={selectedConversationId}
          showCaption={messages.length === 0}
        />
      </main>

      <Modal
        title="Rename conversation"
        open={Boolean(renameConversation)}
        okText="Save"
        onOk={handleRename}
        confirmLoading={renameSaving}
        onCancel={() => setRenameConversation(null)}
      >
        <Input
          autoFocus
          value={renameValue}
          maxLength={255}
          onChange={(event) => setRenameValue(event.target.value)}
          onPressEnter={handleRename}
        />
      </Modal>

      {/* Sources drawer (P0 fix 2): bottom sheet on mobile, right panel on
          desktop. rc-drawer only applies `width` for left/right placement
          and `height` for top/bottom, so both props can be passed
          unconditionally. zIndex 1300 documents intent (above the global
          FAB stack at 1200); src/index.css additionally forces every
          .ant-drawer to 1510 with !important, which is even safer. */}
      <Drawer
        title="Sources and calculations"
        placement={isPC ? "right" : "bottom"}
        width={480}
        height="82vh"
        zIndex={1300}
        open={Boolean(sourcesOpen)}
        onClose={() => setSourcesOpen(null)}
        className="facility-ai-web-drawer"
      >
        {/* Drawer hygiene (3.10): the sources/citations/calculations body is
            expensive (messages.find + several list builds) and used to run
            on EVERY parent render — stream frames included — even with the
            drawer closed. Gating on sourcesOpen skips all of it while closed
            (antd 4 Drawer also lazily mounts children before first open).
            Trade-off: the body blanks at the start of the close slide-out
            instead of riding the animation — acceptable for a dismissible
            side panel. */}
        {sourcesOpen ? (() => {
          const activeMessage =
            messages.find((item) => item.id === sourcesOpen?.messageId) || null;
          const manifest = activeMessage?.response_manifest;
          const citations = Array.isArray(manifest?.citations) ? manifest.citations : [];
          const facts = Array.isArray(manifest?.facts) ? manifest.facts : [];
          const calculations = Array.isArray(manifest?.calculations)
            ? manifest.calculations
            : [];
          const requirements = Array.isArray(manifest?.requirements)
            ? manifest.requirements
            : [];
          const webResults = Array.isArray(activeMessage?.web_results)
            ? activeMessage.web_results
            : [];
          if (!citations.length && !webResults.length) {
            return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No sources." />;
          }
          return (
            <div className="facility-ai-source-panel">
              {requirements.length ? (
                <section>
                  <h3>Answer coverage</h3>
                  <ul className="facility-ai-web-result-list">
                    {requirements.map((requirement) => (
                      <li className="facility-ai-web-result-item" key={requirement.sequence}>
                        <div className="facility-ai-source-heading">
                          <strong>{requirement.part}</strong>
                          <span className={`facility-ai-coverage-status facility-ai-coverage-status--${requirement.status}`}>
                            {formatCoverageStatus(requirement.status)}
                          </span>
                        </div>
                        {requirement.limitation ? <p>{requirement.limitation}</p> : null}
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}
              {citations.length ? (
                <section>
                  <h3>Sources used</h3>
                  <ol className="facility-ai-web-result-list">
                    {citations.map((citation, index) => {
                      const leaf = citationLeaf(citation);
                      const periodText = citation.periods
                        .map((period) =>
                          period.start === period.end
                            ? `${period.label}: ${period.start || ""}`
                            : `${period.label}: ${period.start || ""} – ${period.end || ""}`
                        )
                        .filter(Boolean)
                        .join("; ");
                      return (
                        <li
                          className={`facility-ai-web-result-item${
                            sourcesOpen?.citationId === citation.id ? " is-selected" : ""
                          }`}
                          key={citation.id}
                        >
                          <div className="facility-ai-source-heading">
                            <span className="facility-ai-source-number">{index + 1}</span>
                            {leaf ? (
                              <button
                                type="button"
                                className="facility-ai-source-link"
                                onClick={() => {
                                  const path = buildFacilityPath(leaf, {
                                    companyId,
                                    facilityId,
                                  });
                                  if (path) navigate(path);
                                }}
                              >
                                {citation.label}
                              </button>
                            ) : citation.url ? (
                              <a
                                className="facility-ai-web-result-title"
                                href={citation.url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {citation.label}
                              </a>
                            ) : (
                              <strong>{citation.label}</strong>
                            )}
                          </div>
                          {periodText ? <p>{periodText}</p> : null}
                          {citation.records ? (
                            <p>{formatSourceRecordSummary(citation.records)}</p>
                          ) : null}
                        </li>
                      );
                    })}
                  </ol>
                </section>
              ) : (
                <ol className="facility-ai-web-result-list">
                  {webResults.map((result) => (
                    <li className="facility-ai-web-result-item" key={result.url}>
                      <a
                        className="facility-ai-web-result-title"
                        href={result.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {result.title}
                      </a>
                    </li>
                  ))}
                </ol>
              )}

              {calculations.length ? (
                <section className="facility-ai-calculations">
                  <h3>How this was calculated</h3>
                  {calculations.map((calculation) => (
                    <article key={calculation.id}>
                      <h4>{humanizeEvidenceLabel(calculation.title)}</h4>
                      <p>{calculation.definition}</p>
                      {calculation.method ? (
                        <p><strong>Method:</strong> {calculation.method}</p>
                      ) : null}
                      <details className="facility-ai-calculation-details">
                        <summary>Formula and verification</summary>
                        <p>
                          <strong>Formula:</strong>{" "}
                          <code className="facility-ai-formula-chip">{calculation.formula}</code>
                        </p>
                        {Array.isArray(calculation.checks) && calculation.checks.length ? (
                          <ul className="facility-ai-calculation-checks">
                            {calculation.checks.map((check, index) => (
                              <li key={`${calculation.id}-check-${index}`}>
                                <span>{check.label}</span>
                                <strong className={`facility-ai-check-status facility-ai-check-status--${check.status}`}>
                                  {formatCheckStatus(check.status)}
                                </strong>
                                {check.detail ? <p>{check.detail}</p> : null}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                        {Array.isArray(calculation.limitations) && calculation.limitations.length ? (
                          <div className="facility-ai-calculation-limitations">
                            <strong>Limitations</strong>
                            <ul>
                              {calculation.limitations.map((limitation, index) => (
                                <li key={`${calculation.id}-limitation-${index}`}>{limitation}</li>
                              ))}
                            </ul>
                          </div>
                        ) : null}
                      </details>
                    </article>
                  ))}
                </section>
              ) : null}

              <EvidenceDataUsed facts={facts} citations={citations} />
            </div>
          );
        })() : null}
      </Drawer>
    </div>
  );
};

export default FacilityAiAssistant;
